<template>
  <div class="musubi-page musubi-page--config">
    <header class="musubi-page__head">
      <h1>Mailgun integration</h1>
      <p class="text-muted">
        Outbound delivery + bounce/complaint webhooks. Set credentials below;
        env vars (<code>MAILGUN_API_KEY</code> / <code>MAILGUN_DOMAIN</code> etc.)
        take effect when no override is set here.
      </p>
    </header>

    <div v-if="loading" class="text-muted">Loading…</div>
    <div v-else-if="error" class="alert alert-warning">{{ error }}</div>

    <form v-else class="config-form" @submit.prevent="save">
      <!-- Status banner -->
      <div :class="['status-banner', state.is_complete ? 'status-banner--ok' : 'status-banner--warn']">
        <CygnusIcon :name="state.is_complete ? 'check-circle' : 'warning'" />
        <span v-if="state.is_complete">Mailgun is configured. Ready to send.</span>
        <span v-else>Configuration incomplete — missing API key or domain.</span>
      </div>

      <!-- API key -->
      <label class="config-field">
        <div class="config-field__head">
          <span class="config-field__label">API key</span>
          <span v-if="clears.api_key" class="config-field__hint is-clear">will clear on save</span>
          <span v-else-if="state.env_api_key_set" class="config-field__hint">env-supplied</span>
          <span v-else-if="state.overrides.api_key" class="config-field__hint">override active</span>
        </div>
        <div class="config-field__row">
          <input
            v-model="form.api_key"
            type="password"
            class="form-control"
            autocomplete="new-password"
            :disabled="clears.api_key"
            :placeholder="state.api_key_masked || 'key-...'"
          >
          <button v-if="state.overrides.api_key" type="button" class="btn btn-sm btn-outline-danger" @click="toggleClear('api_key')">
            {{ clears.api_key ? 'Undo' : 'Clear' }}
          </button>
        </div>
        <p class="config-field__help">
          Save the key on its own — no sending domain required. Leave blank to keep the existing key;
          use <strong>Clear</strong> to remove the override + fall back to env.
        </p>
      </label>

      <!-- Domain — OPTIONAL. A key-only save leaves this untouched. -->
      <label class="config-field">
        <div class="config-field__head">
          <span class="config-field__label">Sending domain <span class="config-field__optional">(optional)</span></span>
          <span v-if="state.env_domain_set" class="config-field__hint">env-supplied</span>
          <span v-else-if="state.overrides.domain" class="config-field__hint">override active</span>
        </div>
        <input v-model="form.domain" type="text" class="form-control" placeholder="mg.musubi.apexlstudios.com">
        <p class="config-field__help">
          Optional — registering a sending domain is a separate step. Leave blank to keep the current
          value; clearing a previously-set domain removes the override.
        </p>
      </label>

      <!-- Region -->
      <label class="config-field">
        <div class="config-field__head">
          <span class="config-field__label">Region</span>
          <span v-if="state.env_region_set" class="config-field__hint">env-supplied</span>
          <span v-else-if="state.overrides.region" class="config-field__hint">override active</span>
        </div>
        <select v-model="form.region" class="form-control">
          <option value="us">US (api.mailgun.net)</option>
          <option value="eu">EU (api.eu.mailgun.net)</option>
        </select>
        <p class="config-field__help">
          Match the region you chose at Mailgun account creation. Changing region requires re-verifying domains.
        </p>
      </label>

      <!-- Webhook key -->
      <label class="config-field">
        <div class="config-field__head">
          <span class="config-field__label">Webhook signing key</span>
          <span v-if="clears.webhook_key" class="config-field__hint is-clear">will clear on save</span>
          <span v-else-if="state.env_webhook_key_set" class="config-field__hint">env-supplied</span>
          <span v-else-if="state.overrides.webhook_key" class="config-field__hint">override active</span>
        </div>
        <div class="config-field__row">
          <input
            v-model="form.webhook_key"
            type="password"
            class="form-control"
            autocomplete="new-password"
            :disabled="clears.webhook_key"
            :placeholder="state.webhook_key_masked || '(set in P2.6 when webhook ingest lands)'"
          >
          <button v-if="state.overrides.webhook_key" type="button" class="btn btn-sm btn-outline-danger" @click="toggleClear('webhook_key')">
            {{ clears.webhook_key ? 'Undo' : 'Clear' }}
          </button>
        </div>
        <p class="config-field__help">
          Separate from the API key. Used to verify incoming bounce / complaint webhooks. Find this in Mailgun → Sending → Webhooks.
        </p>
      </label>

      <div class="config-actions">
        <button type="submit" class="btn btn-primary" :disabled="saving">
          <CygnusIcon name="spinner" :spin="true" v-if="saving" />
          <span v-else>Save</span>
        </button>
        <div v-if="saveMessage" :class="['config-actions__msg', saveOk ? 'is-ok' : 'is-error']">
          {{ saveMessage }}
        </div>
      </div>
    </form>

    <!-- Test send — separate form. Recipient + subject + body all
         editable; last-used recipient persists in localStorage so you
         don't retype between visits. Mailgun sandbox 403s if recipient
         isn't on the Authorized Recipients list (whitelist in their
         dashboard). Production-verified domains have no recipient
         restriction. -->
    <form v-if="!loading && !error" class="test-form" @submit.prevent="testSend">
      <header class="test-form__head">
        <h2>Send test email</h2>
        <p class="text-muted">
          Sends one message via the active delivery driver ({{ activeDriverLabel }}) using the current config.
          On failure the provider's response is shown below so you can diagnose without trawling the provider dashboard.
        </p>
      </header>

      <label class="config-field">
        <span class="config-field__label">To</span>
        <input
          v-model="testForm.to"
          type="email"
          class="form-control"
          required
          placeholder="you@example.com"
        >
        <p class="config-field__help">
          On a Mailgun sandbox domain, this address must be on the
          <strong>Authorized Recipients</strong> list (whitelist) — otherwise Mailgun returns 403.
        </p>
      </label>

      <label class="config-field">
        <span class="config-field__label">Subject</span>
        <input v-model="testForm.subject" type="text" class="form-control" placeholder="Yorlin test send">
      </label>

      <label class="config-field">
        <span class="config-field__label">Body (HTML)</span>
        <textarea v-model="testForm.body" rows="4" class="form-control"
          placeholder="<p>This is a test from Yorlin.</p>"></textarea>
      </label>

      <div class="config-actions">
        <button type="submit" class="btn btn-outline-secondary" :disabled="testing || !state.is_complete">
          <CygnusIcon name="spinner" :spin="true" v-if="testing" />
          <span v-else>Send test email</span>
        </button>
        <span v-if="!state.is_complete" class="config-actions__msg is-error">
          Save Mailgun config first.
        </span>
      </div>
    </form>

    <!-- Test-send result panel -->
    <section v-if="testResult" class="test-result">
      <h2>Last test send</h2>
      <div :class="['test-result__banner', testResult.success ? 'is-ok' : 'is-error']">
        <strong v-if="testResult.success">✓ Sent OK</strong>
        <strong v-else>✗ Send failed</strong>
        <span v-if="testResult.http_status">— HTTP {{ testResult.http_status }}</span>
      </div>
      <dl class="test-result__details">
        <dt>Driver</dt>           <dd>{{ testResult.active_driver }}</dd>
        <dt>Provider message ID</dt><dd><code>{{ testResult.provider_message_id || '—' }}</code></dd>
        <dt v-if="testResult.error">Error</dt><dd v-if="testResult.error"><code>{{ testResult.error }}</code></dd>
        <dt v-if="testResult.error">Retryable</dt><dd v-if="testResult.error">{{ testResult.is_retryable ? 'yes' : 'no' }}</dd>
      </dl>
    </section>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

const ENDPOINT = '/api/v1/admin/integrations/mailgun';

export default {
  name: 'AdminMailgunConfig',
  props: {
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      state: {
        is_complete: false,
        api_key_masked: '',
        webhook_key_masked: '',
        domain: '',
        region: 'us',
        env_api_key_set: false,
        env_domain_set: false,
        env_region_set: false,
        env_webhook_key_set: false,
        overrides: { api_key: false, domain: false, region: false, webhook_key: false },
      },
      form: {
        api_key: '',
        domain: '',
        region: 'us',
        webhook_key: '',
      },
      // Diff baseline for the plain fields (domain/region) + staged secret clears.
      originals: { domain: '', region: 'us' },
      clears: {},
      loading: true,
      saving: false,
      testing: false,
      error: null,
      saveMessage: '',
      saveOk: false,
      testResult: null,
      testForm: {
        to: '',
        subject: 'Yorlin test send',
        body: '<p>This is a test message from Yorlin.</p>',
      },
    };
  },
  computed: {
    activeDriverLabel() {
      // V1 hard-codes mailgun. When DeliveryRouter exposes the resolved
      // driver in GET state, wire this up to that.
      return 'Mailgun';
    },
  },
  async mounted() {
    await this.load();
    // Last-used recipient persists across page visits so you don't retype.
    const remembered = (typeof localStorage !== 'undefined') && localStorage.getItem('musubi.mailgun.test-to');
    if (remembered) this.testForm.to = remembered;
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api.get(ENDPOINT);
        this.state = { ...this.state, ...(res?.data ?? res) };
        // Pre-fill non-secret fields so the form shows current value.
        // Secrets stay blank (user types fresh value to update).
        this.form.domain      = this.state.domain || '';
        this.form.region      = this.state.region || 'us';
        this.form.api_key     = '';
        this.form.webhook_key = '';
        // Reset the diff baseline + staged clears to the freshly-loaded state.
        this.originals = { domain: this.form.domain, region: this.form.region };
        this.clears = {};
      } catch (e) {
        this.error = e?.message || 'Failed to load config.';
      } finally {
        this.loading = false;
      }
    },

    toggleClear(field) {
      // Stage/unstage an explicit clear for a secret field (applied on Save via
      // the PUT's `clear` list). Blanking a field can never clear it (blank = keep).
      this.clears = { ...this.clears, [field]: !this.clears[field] };
    },

    async save() {
      this.saving = true;
      this.saveMessage = '';
      this.saveOk = false;

      // NON-CLOBBERING PUT (papercut #42): only send what changed.
      //  - untouched field → OMITTED → backend keeps the stored value;
      //  - removal → EXPLICIT entry in `clear`, never an empty string.
      // The sending domain is OPTIONAL: an api-key-only save omits domain, so the
      // stored domain is left untouched.
      const body = {};
      const clear = [];

      // Secrets: send only a freshly-typed value; a staged Clear (or the legacy
      // '(clear)' sentinel) removes the stored override.
      ['api_key', 'webhook_key'].forEach((key) => {
        if (this.clears[key]) { clear.push(key); return; }
        const v = this.form[key];
        if (v === '(clear)') { clear.push(key); return; }
        if (v) body[key] = v;
      });

      // Plain fields (domain, region): send only when CHANGED; emptying a
      // previously-set domain is an explicit clear.
      ['domain', 'region'].forEach((key) => {
        const cur = this.form[key];
        const orig = this.originals[key] ?? '';
        if (cur === orig) return;                  // unchanged → keep
        if (cur === '') { clear.push(key); return; } // emptied → explicit clear
        body[key] = cur;                           // changed → set
      });

      if (clear.length) body.clear = clear;

      try {
        const res = await this.api.put(ENDPOINT, body);
        const payload = res?.data ?? res;
        if (payload?.success) {
          this.saveOk = true;
          this.saveMessage = 'Saved. ' + (payload.is_complete ? 'Config is complete.' : 'Config is still incomplete.');
          await this.load();
        } else {
          this.saveMessage = payload?.error || 'Save failed.';
        }
      } catch (e) {
        this.saveMessage = e?.response?.data?.error || e?.message || 'Save failed.';
      } finally {
        this.saving = false;
      }
    },

    async testSend() {
      if (!this.testForm.to) return;
      this.testing = true;
      this.testResult = null;
      // Persist the recipient for next visit.
      try { localStorage.setItem('musubi.mailgun.test-to', this.testForm.to); } catch (_) {}

      const body = {
        to:      this.testForm.to,
        subject: this.testForm.subject || 'Yorlin test send',
        body:    this.testForm.body    || '<p>This is a test message from Yorlin.</p>',
      };
      try {
        const res = await this.api.post('/api/v1/admin/email/test-send', body);
        this.testResult = res?.data ?? res;
      } catch (e) {
        // 4xx/5xx come back as axios errors — pull the body off the response.
        this.testResult = e?.response?.data ?? { success: false, error: e?.message || 'Request failed' };
      } finally {
        this.testing = false;
      }
    },
  },
};
</script>

<style>
.musubi-page--config { padding: var(--m-space-6, 32px); max-width: 700px; }

.config-form { display: flex; flex-direction: column; gap: 18px; }
.config-field { display: flex; flex-direction: column; gap: 6px; }
.config-field__head { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.config-field__label { font-size: 0.9rem; font-weight: 500; }
.config-field__hint {
  font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--m-text-tertiary, #94A3B8);
}
.config-field__help { font-size: 0.78rem; color: var(--m-text-secondary, #475569); margin: 0; }
.config-field__optional { font-weight: 400; color: var(--m-text-tertiary, #94A3B8); }
.config-field__hint.is-clear { color: var(--m-status-danger, #EF4444); }
.config-field__row { display: flex; gap: 8px; align-items: center; }
.config-field__row .form-control { flex: 1; }

.status-banner {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; border-radius: var(--m-radius, 6px);
  font-size: 0.88rem;
}
.status-banner--ok { background: #DCFCE7; color: var(--m-status-success, #166534); }
.status-banner--warn { background: #FEF3C7; color: var(--m-status-warning, #92400E); }

.config-actions { display: flex; align-items: center; gap: 10px; margin-top: 4px; }
.config-actions__msg { font-size: 0.85rem; }
.config-actions__msg.is-ok { color: var(--m-status-success, #10B981); }
.config-actions__msg.is-error { color: var(--m-status-danger, #EF4444); }

.test-form {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--m-border, #E2E8F0);
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.test-form__head h2 { font-size: 1rem; margin: 0 0 4px; font-weight: 600; }
.test-form__head .text-muted { color: var(--m-text-secondary, #475569); font-size: 0.85rem; margin: 0; }

.test-result {
  margin-top: 28px;
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius, 6px);
  padding: 16px;
  background: var(--m-bg-card, #fff);
}
.test-result h2 { font-size: 1rem; margin: 0 0 10px; font-weight: 600; }
.test-result__banner { font-size: 0.9rem; margin-bottom: 12px; }
.test-result__banner.is-ok { color: var(--m-status-success, #10B981); }
.test-result__banner.is-error { color: var(--m-status-danger, #EF4444); }
.test-result__details { display: grid; grid-template-columns: 160px 1fr; gap: 6px 12px; font-size: 0.85rem; margin: 0; }
.test-result__details dt { color: var(--m-text-secondary, #475569); }
.test-result__details dd { margin: 0; }
.test-result__details code { background: #F1F5F9; padding: 2px 6px; border-radius: 3px; font-size: 0.82rem; }
</style>
